import { toast } from 'react-toastify';
import { RootState } from 'store/store';
import { useAppDispatch, useAppStore } from 'store/store-hooks';
import { StoryBoard, StoryBoardTracks } from 'services/storyboard.service';
import {
  createClipId,
  getClipTime,
  getSbCopy,
  getSbTrackIndex,
  getSbTracks,
  roundTime,
  selectClipData,
} from 'components/app/_helpers/_app.helper';
import { useAudioTrackContext } from 'components/app/audio-track/_mediator/audio-track.provider';
import {
  Clip,
  ClipsMap,
  EMPTY_CELL,
  HandleClips,
  LinkedClips,
  UNLINKED_ID,
  handleClips,
  Selector,
} from 'components/app/editor/_mediator/editor.slice';
import { BackupParams } from 'components/app/editor/_mediator/backup/backup.slice';
import { useMarkersCb } from 'components/app/editor/_mediator/editor-markers/use-markers-cb';
import { getCellMarkersIndex } from 'components/app/editor/_mediator/editor-markers/editor-markers.helper';
import { ResizePosition, useCellResizePayload } from 'components/app/storyboard/sb-cell-resize/use-cell-resize-payload';
import {
  findLinkByClipTime,
  generateLinkId,
  isTimeGetIntoGroup,
  selectLinkedGroup,
} from './editor-clips-linking.helper';
import { clipsApi, linksApi } from './editor-clips.helper';

interface Payload {
  markers: MarkerId[];
  storyBoard: StoryBoard;
  linkedClips: LinkedClips;
  selector: Selector;
  clipsMap: ClipsMap;
}

interface ShiftResize {
  position: ResizePosition;
  straddledMarkers: Index[];
  snapTo: Index | null;
}

export const useShiftResize = (clip: Clip | null, cell: Index) => {
  const { wsGetCursorTime } = useAudioTrackContext();
  const { addMarkers } = useMarkersCb();
  const getCellResizeCb = useCellResizePayload(clip, cell);
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const getMarkersPayload = ({ snapTo }: ShiftResize): Payload | undefined => {
    const { markers, storyBoard, selector, linkedClips, duration } = selectData(store.getState());
    if (!clip || !markers || !selector || !duration) return;

    const res = { markers, storyBoard, selector, linkedClips, clipsMap: {} };
    if (snapTo !== null) return res;

    const wsTime = wsGetCursorTime();
    const markersRes = addMarkers([wsTime], { dispatch: false });
    if (!markersRes) return;

    return { ...res, ...markersRes };
  };

  const getClipsPayload = (
    params: ShiftResize,
    payload: Payload
  ): { data: HandleClips; backupParams?: BackupParams } | undefined => {
    const resizeCb = getCellResizeCb(params.position);
    const { track, markers, clipsMap, selector, duration } = selectData(store.getState());
    if (!clip || !markers || !resizeCb || !duration) return;

    const cells = params.straddledMarkers.length
      ? getCellsStraddled(params)
      : getCells(params, wsGetCursorTime(), clip, cell, markers);
    if (!cells) return { data: payload };

    const displaceRes = displaceClips(cells, track, payload.storyBoard);
    if (!displaceRes) return;

    const res = { ...payload, storyBoard: displaceRes };

    let err = false;

    const clips = cells.map((cell) => {
      const markerTime = getMarkerTime(cell, clip, res.markers, duration, params);
      const clipTime = roundTime(resizeCb.getWsTimeData(markerTime).clipTime);
      if (findLinkByClipTime(clipTime, res.linkedClips, clip.mediaFileId)) err = true;
      return createClip(clipTime, clip);
    });

    const isClipLinked = clip.clipGroupId !== UNLINKED_ID;
    const linkId = isClipLinked ? clip.clipGroupId : generateLinkId(res.linkedClips);
    const sbPosition = { cell: params.position === 'right' ? cell : cells[0], track };
    const clipsRes = clipsApi(res.selector, res.storyBoard, res.linkedClips).add(clips, sbPosition);
    const linksRes = isClipLinked ? {} : linksApi(res.linkedClips).link([clip, ...clips], linkId);
    const storyBoard = { ...res.storyBoard, ...clipsRes.storyBoard };
    const result = { ...res, ...clipsRes, ...linksRes, storyBoard };

    const clipsTime = selector![clip.mediaFileId]
      .map((clipId) => selectClipData(clipId, clipsMap!))
      .filter((el) => el.clipGroupId === UNLINKED_ID && el.clipId !== clip.clipId)
      .map((clip) => getClipTime(clip));

    err = isTimeGetIntoGroup(clipsTime, selectLinkedGroup(linkId, result.linkedClips));

    if (err) {
      toast.error('new clips cannot be linked');
      return;
    }

    const isLeft = params.position === 'left';
    const activeClipId = isLeft
      ? Object.values(result.linkedClips)[0][0]
      : (Object.values(result.linkedClips)[0].at(-1) as ClipId);

    const backupParams: BackupParams = {
      title: 'Clip Resize Shift',
      action: isLeft ? undefined : 'resizeRight',
      activeClip: [clip.clipId, activeClipId],
    };

    return { data: result, backupParams };
  };

  return (params: ShiftResize) => {
    const res = getMarkersPayload(params);
    if (!res) return;
    const result = getClipsPayload(params, res);
    if (!result) return;
    dispatch(handleClips({ ...result }));
  };
};

/* H E L P E R S */

const getDraggingMarker = (position: ResizePosition, markers: MarkerId[], cell: Index) => {
  const { leftIndex, rightIndex } = getCellMarkersIndex(cell, markers?.length);
  return position === 'left' ? leftIndex : rightIndex;
};

const getCellsStraddled = (params: ShiftResize) => {
  const { snapTo, position, straddledMarkers } = params;
  const isStart = position === 'left';
  const offset = isStart ? (snapTo !== null ? 1 : 2) : 0;
  const cells = straddledMarkers.map((el) => el + offset);
  if (snapTo === null || snapTo === -1) isStart ? cells.unshift(cells[0] - 1) : cells.push(cells[cells.length - 1] + 1);
  return cells;
};

const getCells = (params: ShiftResize, wsTime: Seconds, clip: Clip, cell: Index, markers: MarkerId[]) => {
  const { position, snapTo } = params;
  const draggingMarker = getDraggingMarker(position, markers, cell);
  if (draggingMarker === null) return clip.endTime !== null ? [cell] : [cell + 1];
  if (position === 'left') {
    if (snapTo === -1) return [cell - 1];
    if (clip.endTime !== null) return [cell];
    if (wsTime < markers[draggingMarker]) return [cell];
    return [cell + 1];
  }
  if (position === 'right') {
    if (snapTo === -1) return [cell + 1];
    if (clip.endTime !== null) {
      if (wsTime < markers[draggingMarker]) return [cell];
      return [cell + 1];
    }
    return [cell + 1];
  }
};

const getMarkerTime = (cell: Index, clip: Clip, markers: MarkerId[], duration: Seconds, params: ShiftResize) => {
  const { snapTo, position } = params;
  const { leftIndex, rightIndex } = getCellMarkersIndex(cell, markers.length);
  const markerIndex = clip.endTime === null ? leftIndex : rightIndex;
  if (markerIndex === null && snapTo === -1) return position === 'left' ? 0 : duration;
  return markerIndex !== null ? markers[markerIndex] : position === 'left' ? markers[0] : markers[markers.length - 1];
};

const createClip = (time: Seconds, clip: Clip) => {
  const newClip: Clip = { ...clip };
  if (clip.endTime === null) newClip.startTime = time;
  else newClip.endTime = time;
  newClip.clipId = createClipId(newClip);
  return newClip;
};

const displaceClips = (cells: Index[], trackOrigin: StoryBoardTracks, sbOrigin: StoryBoard) => {
  const notEmptyCells: Index[] = cells.filter((cell) => sbOrigin[trackOrigin][cell] !== EMPTY_CELL);
  const len = sbOrigin.clipBoard.length;
  const sbRes = getSbCopy(sbOrigin);

  const displace = (cell: Index, track: StoryBoardTracks, reverse?: 'reverse') => {
    if (sbRes[track][cell] !== EMPTY_CELL) return;
    const cellOrigin = (reverse ? notEmptyCells.pop() : notEmptyCells.shift()) as Index;
    const clipId = sbRes[trackOrigin][cellOrigin];
    sbRes[trackOrigin][cellOrigin] = EMPTY_CELL;
    sbRes[track][cell] = clipId;
  };

  getSbTracks()
    .slice(getSbTrackIndex(trackOrigin))
    .forEach((track) => {
      const from = trackOrigin === track ? cells[cells.length - 1] + 1 : 0;
      for (let cell = from; cell < len; cell++) {
        if (!notEmptyCells.length) break;
        displace(cell, track);
      }
    });
  if (!notEmptyCells.length) return sbRes;

  getSbTracks()
    .slice(0, getSbTrackIndex(trackOrigin) + 1)
    .reverse()
    .forEach((track) => {
      const from = trackOrigin === track ? cells[0] - 1 : len - 1;
      for (let cell = from; cell >= 0; cell--) {
        if (!notEmptyCells.length) break;
        displace(cell, track, 'reverse');
      }
    });
  if (!notEmptyCells.length) return sbRes;
};

/* S E L E C T O R */

const selectData = (state: RootState) => {
  const { duration } = state.audio;
  const { track } = state.editor__controls.sbPosition;
  const { markers, storyBoard, selector, linkedClips, clipsMap } = state.editor;
  return { markers, storyBoard, selector, linkedClips, duration, track, clipsMap };
};
